import { apiClient } from '../../../lib/api/client';

export interface BusinessOverviewCountAmount {
  count: number;
  amount: number;
}

export interface BusinessOverviewLeadSummary {
  open: BusinessOverviewCountAmount;
  won: BusinessOverviewCountAmount;
  lost: BusinessOverviewCountAmount;
}

export interface BusinessOverviewReferralSummary {
  referral: BusinessOverviewCountAmount;
  nonReferral: BusinessOverviewCountAmount;
  referralWon: BusinessOverviewCountAmount;
  referralLost: BusinessOverviewCountAmount;
}

export interface BusinessOverviewContactsSummary {
  totalContacts: number;
  totalAgents: number;
}

export interface BusinessOverviewDocumentsSummary {
  acceptedInPeriod: number;
  expiringWithin30Days: number;
}

export interface BusinessOverviewCampaignsSummary {
  totalActiveCampaigns: number;
  totalBudget: number;
  targetCompletionRatio: number;
}

export interface BusinessOverviewTasksSummary {
  openTasks: number;
  completedTasks: number;
  completedOnTime: number;
  completedAfterDueDate: number;
}

export interface BusinessOverviewFollowUpsSummary {
  openFollowUps: number;
  completedInPeriod: number;
  upcomingIn7Days: number;
}

export interface BusinessOverviewCommissionsSummary {
  periodTotal: number;
  allTimeTotal: number;
}

export interface BusinessOverviewBantSummary {
  averageScore: number;
  scoredLeadCount: number;
}

export interface BusinessOverviewFunnelStage {
  stageId: number;
  stageLabel: string;
  dealCount: number;
  totalAmount: number;
  weightedAmount: number;
}

export interface BusinessOverviewFunnelBreakdownStage extends BusinessOverviewFunnelStage {
  conversionPercentFromPrevious: number | null;
}

export interface BusinessOverviewResponse {
  leadSummary: BusinessOverviewLeadSummary;
  referralSummary: BusinessOverviewReferralSummary;
  contacts: BusinessOverviewContactsSummary;
  documents: BusinessOverviewDocumentsSummary;
  campaigns: BusinessOverviewCampaignsSummary;
  tasks: BusinessOverviewTasksSummary;
  followUps: BusinessOverviewFollowUpsSummary;
  commissions: BusinessOverviewCommissionsSummary;
  bant: BusinessOverviewBantSummary;
  pipelineFunnel: BusinessOverviewFunnelStage[];
  funnelBreakdown: BusinessOverviewFunnelBreakdownStage[];
}

export interface GetBusinessOverviewParams {
  fromUtc?: string;
  toUtc?: string;
  segmentIds?: string[];
  contactCategoryIds?: number[];
  assigneeIds?: string[];
}

export async function getBusinessOverview(
  params?: GetBusinessOverviewParams
): Promise<BusinessOverviewResponse> {
  const searchParams = new URLSearchParams();
  if (params?.fromUtc) searchParams.set('fromUtc', params.fromUtc);
  if (params?.toUtc) searchParams.set('toUtc', params.toUtc);
  if (params?.segmentIds?.length) {
    for (const id of params.segmentIds) searchParams.append('segmentIds', id);
  }
  if (params?.contactCategoryIds?.length) {
    for (const id of params.contactCategoryIds) searchParams.append('contactCategoryIds', String(id));
  }
  if (params?.assigneeIds?.length) {
    for (const id of params.assigneeIds) searchParams.append('assigneeIds', id);
  }
  const response = await apiClient.get<BusinessOverviewResponse>(
    `/dashboard/business-overview?${searchParams.toString()}`
  );
  return response.data;
}
